import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    const params = showDeactivated ? "?include_deactivated=true" : "";
    const { data } = await api.get(`/users${params}`);
    setUsers(data);
  }, [showDeactivated]);
  useEffect(() => { load(); }, [load]);

  const updateRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}/role?role=${role}`);
      toast.success(`${u.name} → ${role}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success(`${toDelete.name} deactivated`);
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to deactivate");
    }
  };

  const reactivate = async (u) => {
    try {
      await api.post(`/users/${u.id}/reactivate`);
      toast.success(`${u.name} reactivated`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="users-title">Users & Access</h1>
          <p className="text-slate-600 mt-1">Every account across the platform. Deactivating a user preserves their historical records.</p>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="show-deact" checked={showDeactivated} onCheckedChange={setShowDeactivated} data-testid="users-toggle-deactivated" />
          <Label htmlFor="show-deact" className="text-sm text-slate-600 cursor-pointer">Show deactivated</Label>
        </div>
      </div>

      <div className="rounded-md border tf-hairline bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const isSelf = u.id === currentUser?.id;
              const isActive = u.is_active !== false;
              return (
                <tr key={u.id} className={`border-t tf-hairline hover:bg-slate-50 ${isActive ? "" : "opacity-60"}`}>
                  <td className="p-3">
                    {u.name}
                    {isSelf && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">you</span>}
                  </td>
                  <td className="p-3 font-mono text-xs">{u.email}</td>
                  <td className="p-3">
                    <Select value={u.role} onValueChange={(v) => updateRole(u, v)} disabled={isSelf || !isActive}>
                      <SelectTrigger className="w-40 h-8 text-xs" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="cpa">CPA</SelectItem>
                        <SelectItem value="caller">Caller</SelectItem>
                        <SelectItem value="document_team">Document Team</SelectItem>
                        <SelectItem value="client">Client</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3">
                    <span className={`text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded ${isActive ? "bg-emerald-50 text-emerald-700" : "bg-slate-200 text-slate-700"}`}>
                      {isActive ? "Active" : "Deactivated"}
                    </span>
                  </td>
                  <td className="p-3 font-mono text-xs">{(u.created_at || "").split("T")[0]}</td>
                  <td className="p-3 text-right">
                    {isActive ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={isSelf}
                        onClick={() => setToDelete(u)}
                        data-testid={`remove-user-${u.id}`}
                      >
                        <Trash size={14} className={isSelf ? "text-slate-300" : "text-[#D32F2F]"} />
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => reactivate(u)} data-testid={`reactivate-user-${u.id}`}>
                        <ArrowClockwise size={14} className="text-[#0A2540]" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Their login is revoked immediately. All historical records (returns, notices, payments, documents) remain intact and still reference their name. You can reactivate them at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-user-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="remove-user-confirm">
              Deactivate user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
